import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { alpha } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { GridFilterItem, GridFilterModel } from '@mui/x-data-grid';
import PillTabs from 'components/common/PillTabs';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { RefreshIcon } from '../../assets/icons';
import MasterFilter, {
  type DateRangeValue,
  type FilterFieldConfig,
  useMasterFilter,
} from '../../components/common/MasterFilter';
import SmartFilterModal from '../../components/common/MasterFilter/SmartFilterModal';
import { gridItemsToActiveFilters, removeChipItems } from '../../components/common/MasterFilter/smartFilterMapping';
import { useAssignableUsers } from '../../components/common/MasterFilter/useAssignableUsers';
import { useLeadFilterOptions } from '../../components/common/MasterFilter/useLeadFilterOptions';
import { useSelectedBusinessUnit } from '../../contexts/SidebarContext';
import useCurrentUser from '../../hooks/useCurrentUser';
import useKanbanFilters from '../../hooks/useKanbanFilters';
import useKanbanPagination from '../../hooks/useKanbanPagination';
import { useGetBusinessUnitQuery } from '../../services/api/businessUnits.api';
import { MOBILE_PAGE_PT, NAVY, SURFACE } from '../../styles/tokens';
import { INSUREX_BUSINESS_UNIT_ID } from '../../utils/constants';
import KanbanBoard from './components/KanbanBoard';
import LeadsTable from './components/LeadsTable';
import leadsFilterFields from './leadsFilterFields';

const DATE_RANGE_FIELD: FilterFieldConfig = {
  key: 'dateRange',
  kind: 'dateRange',
  label: 'Fecha creación',
  placement: 'bar',
  defaultValue: { start: moment().startOf('month'), end: moment().endOf('day') },
};

const Leads = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useCurrentUser();

  const queryParams = new URLSearchParams(window.location.search);
  const queryViewMode = queryParams.get('viewMode');
  const { selectedBusinessUnitId: businessUnitId, setSelectedBusinessUnitId: setBusinessUnitId } =
    useSelectedBusinessUnit();

  // Fetch business unit type
  const { data: businessUnitData } = useGetBusinessUnitQuery(businessUnitId ?? 0, {
    skip: !businessUnitId,
  });
  const businessUnitType = businessUnitData?.type || null;

  // Check if user is sellerWithAssignment from mueve seguro
  const isSellerWithAssignmentFromMueveSeguro =
    currentUser?.role === 'sellerWithAssignment' && currentUser?.businessUnitIds?.includes(INSUREX_BUSINESS_UNIT_ID);

  const getInitialViewMode = (): 'table' | 'kanban' => {
    if (queryViewMode === 'table') return 'table';
    return 'kanban';
  };
  const [viewMode, setViewMode] = useState<'table' | 'kanban'>(getInitialViewMode());
  const [tableTotal, setTableTotal] = useState<number>(0);
  const [refreshNonce, setRefreshNonce] = useState(0);

  // ── Shared filter state (used by both kanban and table) ──────────────────
  const queryAdvancedFilterModel =
    queryParams.get('leads.advancedFilterModel') ||
    queryParams.get('kanbanFilterModel') ||
    queryParams.get('filterModel');
  const [advancedFilterModel, setAdvancedFilterModel] = useState<GridFilterModel>(() => {
    const parsed: GridFilterModel = queryAdvancedFilterModel
      ? (JSON.parse(queryAdvancedFilterModel) as GridFilterModel)
      : { items: [] };
    // When seeding from the legacy `filterModel` param (report/dashboard links), strip createdAt items
    // — those belong to the date-range bar and are initialised there via DATE_RANGE_FIELD.defaultValue.
    const isFromLegacyFilterModel =
      !queryParams.get('leads.advancedFilterModel') &&
      !queryParams.get('kanbanFilterModel') &&
      !!queryParams.get('filterModel');
    if (isFromLegacyFilterModel) {
      parsed.items = parsed.items.filter((i) => i.field !== 'createdAt');
    }
    // Always exclude discarded leads by default
    const hasDiscarded = parsed.items.some((i) => i.field === 'discarded');
    if (!hasDiscarded) {
      parsed.items.push({ field: 'discarded', operator: 'equals', value: false });
    }
    return parsed;
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  useKanbanFilters(advancedFilterModel);

  // Shared bar config builder: search + date range + source/brand(conditional)/topics.
  const buildBarConfig = useMemo(
    (): FilterFieldConfig[] => [
      {
        key: 'searchTerm',
        kind: 'text',
        label: 'Buscar',
        placement: 'bar',
        placeholder: 'ID, Nombre, teléfono, email o RUT',
      },
      DATE_RANGE_FIELD,
      { key: 'sources', kind: 'multiSelect', label: 'Origen', placement: 'bar' },
      ...(businessUnitType === 'car_dealership'
        ? [{ key: 'currentBrands', kind: 'multiSelect' as const, label: 'Marca interés', placement: 'bar' as const }]
        : []),
      { key: 'topics', kind: 'multiSelect', label: 'Tags', placement: 'bar' },
    ],
    [businessUnitType]
  );

  const masterFilter = useMasterFilter(buildBarConfig, businessUnitId, undefined, undefined, {
    urlNamespace: 'leads',
  });

  // Derive GridFilterItems from bar multi-selects
  const barFilterItems = useMemo((): GridFilterItem[] => {
    const items: GridFilterItem[] = [];
    const sources = masterFilter.values.sources as string[] | undefined;
    if (sources?.length) {
      items.push({ field: 'source', operator: 'in', value: sources.join(',') });
    }
    const currentBrands = masterFilter.values.currentBrands as string[] | undefined;
    if (currentBrands?.length) {
      items.push({ field: 'currentBrand', operator: 'in', value: currentBrands.join(',') });
    }
    const topics = masterFilter.values.topics as string[] | undefined;
    if (topics?.length) items.push({ field: 'topics', operator: 'in', value: topics.join(',') });
    return items;
  }, [masterFilter.values.sources, masterFilter.values.currentBrands, masterFilter.values.topics]);

  // Single source of truth for the kanban date range: MasterFilter.
  // Memoize ISO strings so RTK Query keys stay stable across renders, avoiding
  // duplicate /leads/uniqueValues + /leads/kanban fetches on mount.
  const kanbanDateRange = masterFilter.values.dateRange as DateRangeValue | undefined;
  const startDateIso = useMemo(() => kanbanDateRange?.start?.toISOString() ?? null, [kanbanDateRange?.start]);
  const endDateIso = useMemo(() => kanbanDateRange?.end?.toISOString() ?? null, [kanbanDateRange?.end]);
  const searchTerm = (masterFilter.values.searchTerm as string | undefined)?.trim() || undefined;

  const kanbanFilterModel = useMemo(
    () => JSON.stringify({ items: [...barFilterItems, ...advancedFilterModel.items] }),
    [barFilterItems, advancedFilterModel.items]
  );

  const kanbanPagination = useKanbanPagination(businessUnitId, {
    startDate: startDateIso,
    endDate: endDateIso,
    filterModel: kanbanFilterModel,
    searchTerm,
    skip: viewMode !== 'kanban',
  });

  // Persist advancedFilterModel to URL so the smart-filter selection survives navigating into
  // a lead/client detail and back. The default-only state (just the always-on `discarded=false`
  // row) is omitted to keep URLs tidy.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const before = params.toString();
    const isDefaultOnly = advancedFilterModel.items.every((i) => i.field === 'discarded' && i.value === false);
    if (isDefaultOnly) {
      params.delete('leads.advancedFilterModel');
    } else {
      params.set('leads.advancedFilterModel', JSON.stringify(advancedFilterModel));
    }
    // Drop legacy keys once their payload has been migrated to the namespaced one.
    params.delete('kanbanFilterModel');
    params.delete('filterModel');
    if (params.toString() === before) return;
    navigate({ pathname: window.location.pathname, search: `?${params.toString()}` }, { replace: true });
  }, [advancedFilterModel, navigate]);

  // Seed bar filters from URL `filterModel` once on mount (e.g. dashboard sankey redirects).
  // Without this, the bar's dateRange keeps its last-7-days default and intersects the
  // URL-provided createdAt, wiping the intended range.
  // Skipped when the URL already carries namespaced `leads.*` params — those win and are
  // already applied by the bar's lazy init.
  const hasSeededFromUrlRef = useRef(false);
  useEffect(() => {
    if (hasSeededFromUrlRef.current) return;
    const search = new URLSearchParams(window.location.search);
    const hasNamespacedParams = Array.from(search.keys()).some((k) => k.startsWith('leads.'));
    if (hasNamespacedParams) {
      hasSeededFromUrlRef.current = true;
      return;
    }
    const raw = search.get('filterModel');
    if (!raw) return;
    let parsed: GridFilterModel | null = null;
    try {
      parsed = JSON.parse(raw) as GridFilterModel;
    } catch {
      return;
    }
    if (!parsed?.items?.length) return;
    hasSeededFromUrlRef.current = true;

    const gt = parsed.items.find((i) => i.field === 'createdAt' && i.operator === 'greaterThan');
    const lt = parsed.items.find((i) => i.field === 'createdAt' && i.operator === 'lessThan');
    const startDateParam = new URLSearchParams(window.location.search).get('startDate');
    const endDateParam = new URLSearchParams(window.location.search).get('endDate');
    const startVal = gt?.value ?? startDateParam;
    const endVal = lt?.value ?? endDateParam;
    if (startVal || endVal) {
      // Snap to full-day bounds so a bare `YYYY-MM-DD` deep-link end still includes the
      // closing date; already end-of-day ISO values are left unchanged (idempotent).
      const dateRange = {
        start: startVal ? moment(startVal as string).startOf('day') : null,
        end: endVal ? moment(endVal as string).endOf('day') : null,
      };
      masterFilter.setValue('dateRange', dateRange);
    }

    const pickMulti = (field: string) => {
      const item = parsed?.items.find((i) => i.field === field && i.operator === 'in');
      if (!item?.value) return null;
      return String(item.value).split(',').filter(Boolean);
    };
    const sourceParam = new URLSearchParams(window.location.search).get('source');
    const sources = pickMulti('source') ?? (sourceParam ? [sourceParam] : null);
    if (sources?.length) {
      masterFilter.setValue('sources', sources);
    }
    const currentBrands = pickMulti('currentBrand') ?? pickMulti('brands');
    if (currentBrands?.length) {
      masterFilter.setValue('currentBrands', currentBrands);
    }
    const initialBrands = pickMulti('initialBrandOfInterest');
    if (initialBrands?.length) {
      setAdvancedFilterModel((prev) => {
        const withoutInitial = prev.items.filter((i) => i.field !== 'initialBrandOfInterest');
        return {
          ...prev,
          items: [
            ...withoutInitial,
            { field: 'initialBrandOfInterest', operator: 'in', value: initialBrands.join(',') },
          ],
        };
      });
    }
    const topics = pickMulti('topics');
    if (topics?.length) {
      masterFilter.setValue('topics', topics);
    }
  }, [masterFilter.setValue]);

  // ── Shared dynamic options ───────────────────────────────────────────────
  // Modal-only options (origin, branchOffices, campaigns, assignableUsers) are deferred
  // until needed to avoid eager requests on initial render. They become eager when:
  //   - the advanced filter modal is open (need them to populate fields), or
  //   - the user has an active advanced filter on that field (need them for chip labels).
  // The table's "Campaña" column dropdown degrades to a free-text filter when campaigns
  // aren't loaded, so we don't force-load campaigns for table view.
  const hasOriginFilter = advancedFilterModel.items.some((i) => i.field === 'origin');
  const hasBranchOfficeFilter = advancedFilterModel.items.some((i) => i.field === 'branchOffice');
  const hasCampaignFilter = advancedFilterModel.items.some(
    (i) => i.field === 'whatsAppCampaign' || i.field === 'outboundCampaignId'
  );
  const hasAssignedUserFilter = advancedFilterModel.items.some((i) => i.field === 'clientUserId');
  const leadFilterOptions = useLeadFilterOptions(businessUnitId, startDateIso, endDateIso, 'lead', {
    skipOrigins: !isFilterOpen && !hasOriginFilter,
    skipBranchOffices: !isFilterOpen && !hasBranchOfficeFilter,
    skipCampaigns: !isFilterOpen && !hasCampaignFilter,
  });
  const assignableUsers = useAssignableUsers(businessUnitId, {
    skip: !isFilterOpen && !hasAssignedUserFilter,
  });
  const smartFilterDynamicOptions = useMemo(
    () => ({
      sources: leadFilterOptions.sources,
      whatsAppCampaigns: leadFilterOptions.campaigns,
      brands: leadFilterOptions.brands,
      currentBrands: leadFilterOptions.currentBrands,
      topics: leadFilterOptions.topics,
      branchOffices: leadFilterOptions.branchOffices,
      origins: leadFilterOptions.origins,
      assignedUser: assignableUsers,
    }),
    [leadFilterOptions, assignableUsers]
  );

  const externalChips = useMemo(
    () => gridItemsToActiveFilters(advancedFilterModel.items, leadsFilterFields, smartFilterDynamicOptions),
    [advancedFilterModel.items, smartFilterDynamicOptions]
  );

  // Keep viewMode in sync with URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const vm = params.get('viewMode');
    if (vm === 'table' && viewMode !== 'table') setViewMode('table');
    if (vm === 'kanban' && viewMode !== 'kanban') setViewMode('kanban');
  }, [location.search, viewMode]);

  // Sync the selected business unit with the URL. If the URL carries a BU (e.g. arrived
  // here from a "Ver Leads" link in another tab), adopt it into context so the first
  // /leads query already uses the correct BU. Otherwise mirror the context BU into the URL.
  const hasSyncedBusinessUnitFromUrl = useRef(false);
  useEffect(() => {
    if (hasSyncedBusinessUnitFromUrl.current) return;
    const urlBu = new URLSearchParams(window.location.search).get('businessUnitId');
    if (urlBu && urlBu !== String(businessUnitId)) {
      hasSyncedBusinessUnitFromUrl.current = true;
      setBusinessUnitId(Number(urlBu));
    } else {
      hasSyncedBusinessUnitFromUrl.current = true;
    }
  }, [businessUnitId, setBusinessUnitId]);

  useEffect(() => {
    if (businessUnitId !== null) {
      const searchParams = new URLSearchParams(window.location.search);
      const currentBusinessUnitId = searchParams.get('businessUnitId');
      if (currentBusinessUnitId !== businessUnitId.toString()) {
        searchParams.set('businessUnitId', businessUnitId.toString());
        navigate(`${window.location.pathname}?${searchParams.toString()}`, { replace: true });
      }
    }
  }, [businessUnitId, navigate]);

  // Auto-set business unit for mueve seguro users
  useEffect(() => {
    if (isSellerWithAssignmentFromMueveSeguro && !businessUnitId) {
      setBusinessUnitId(INSUREX_BUSINESS_UNIT_ID);
    }
  }, [isSellerWithAssignmentFromMueveSeguro, businessUnitId, setBusinessUnitId]);

  const handleViewModeChange = (newViewMode: 'table' | 'kanban') => {
    setViewMode(newViewMode);
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('viewMode', newViewMode);
    navigate(`${window.location.pathname}?${searchParams.toString()}`, { replace: true });
  };

  const handleAdvancedFilterChange = (items: GridFilterItem[]) => setAdvancedFilterModel({ items });

  const handleShowDiscardedChange = useCallback((show: boolean) => {
    setAdvancedFilterModel((prev) => {
      const withoutDiscarded = prev.items.filter((i) => i.field !== 'discarded');
      const next = show
        ? withoutDiscarded
        : [...withoutDiscarded, { field: 'discarded', operator: 'equals', value: false }];
      return { ...prev, items: next };
    });
  }, []);

  const isRefreshing = viewMode === 'kanban' ? kanbanPagination.isFetching : false;
  const handleRefresh = useCallback(() => {
    if (viewMode === 'kanban') {
      kanbanPagination.refreshAllLeads();
    } else {
      setRefreshNonce((n) => n + 1);
    }
  }, [viewMode, kanbanPagination.refreshAllLeads]);

  const tableBarFilterItems = useMemo((): GridFilterItem[] => {
    const items: GridFilterItem[] = [];
    const dr = masterFilter.values.dateRange as DateRangeValue | undefined;
    if (dr?.start) items.push({ field: 'createdAt', operator: 'greaterThan', value: dr.start.toISOString() });
    if (dr?.end) items.push({ field: 'createdAt', operator: 'lessThan', value: dr.end.toISOString() });
    const sources = masterFilter.values.sources as string[] | undefined;
    if (sources?.length) {
      items.push({ field: 'source', operator: 'in', value: sources.join(',') });
    }
    const currentBrands = masterFilter.values.currentBrands as string[] | undefined;
    if (currentBrands?.length) {
      items.push({ field: 'currentBrand', operator: 'in', value: currentBrands.join(',') });
    }
    const topics = masterFilter.values.topics as string[] | undefined;
    if (topics?.length) items.push({ field: 'topics', operator: 'in', value: topics.join(',') });
    return items;
  }, [masterFilter.values]);

  const renderContent = () => {
    const hasBusinessUnit = businessUnitId !== null && businessUnitId !== -1;
    if (!hasBusinessUnit && (currentUser?.isAdmin || viewMode === 'kanban')) {
      return (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="h6" sx={{ color: `${NAVY}99` }}>
            Selecciona una unidad de negocio para ver los leads
          </Typography>
        </Box>
      );
    }

    if (viewMode === 'table') {
      return (
        <LeadsTable
          businessUnitId={businessUnitId}
          businessUnitType={businessUnitType}
          externalFilterItems={[...tableBarFilterItems, ...advancedFilterModel.items]}
          searchTerm={searchTerm}
          onTotalChange={setTableTotal}
          onShowDiscardedChange={handleShowDiscardedChange}
          refreshNonce={refreshNonce}
          sourceOptions={leadFilterOptions.sources}
          brandOptions={leadFilterOptions.brands}
          campaignOptions={leadFilterOptions.campaigns}
        />
      );
    }

    return (
      <KanbanBoard
        leadsByStatus={kanbanPagination.leadsByStatus}
        statusCounts={kanbanPagination.statusCounts}
        paginationInfo={kanbanPagination.paginationInfo}
        loadingStates={kanbanPagination.loadingStates}
        isInitialLoading={kanbanPagination.isInitialLoading}
        businessUnitId={businessUnitId}
        statusConfigProp={businessUnitData?.statusConfig}
        conversationTaggingConfig={businessUnitData?.conversationTaggingConfig}
        onLoadMore={kanbanPagination.loadMoreLeads}
        getSkeletonCount={kanbanPagination.getSkeletonCount}
      />
    );
  };

  const viewToggle = !isSellerWithAssignmentFromMueveSeguro && (
    <PillTabs
      tabs={[
        { value: 'kanban', label: 'Tablero' },
        { value: 'table', label: 'Lista' },
      ]}
      value={viewMode}
      onChange={handleViewModeChange}
      ariaLabel="Vista de leads"
    />
  );

  const totalForCurrentView = viewMode === 'kanban' ? kanbanPagination.totalLeads : tableTotal;

  const refreshButton = (
    <Tooltip title="Actualizar lista" arrow>
      <IconButton
        onClick={isRefreshing ? undefined : handleRefresh}
        disabled={isRefreshing}
        size="small"
        aria-label="Actualizar lista"
        sx={{
          color: alpha(NAVY, 0.55),
          transition: 'all 0.15s ease-in-out',
          '&:hover': { bgcolor: alpha(NAVY, 0.06) },
        }}
      >
        <RefreshIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  const toggleBar = !isSellerWithAssignmentFromMueveSeguro && (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 1.5 }}>
      {totalForCurrentView > 0 ? (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          📊 {totalForCurrentView} leads total
        </Typography>
      ) : (
        <Box />
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {refreshButton}
        {viewToggle}
      </Box>
    </Box>
  );

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: SURFACE }}>
      {/* Shared MasterFilter */}
      {(viewMode === 'table' || businessUnitId) && (
        <Box sx={{ px: 2, pt: { xs: `${MOBILE_PAGE_PT}px`, md: 1.5 }, flexShrink: 0 }}>
          <MasterFilter
            {...masterFilter}
            dynamicOptions={smartFilterDynamicOptions}
            onAdvancedClick={() => setIsFilterOpen(true)}
            advancedFilterCount={advancedFilterModel.items.length}
            externalChips={externalChips}
            onRemoveExternalChip={(key) =>
              handleAdvancedFilterChange(removeChipItems(advancedFilterModel.items, leadsFilterFields, key))
            }
            onClearAllExternalChips={() => handleAdvancedFilterChange([])}
          />
          <SmartFilterModal
            open={isFilterOpen}
            onClose={() => setIsFilterOpen(false)}
            fieldDefs={leadsFilterFields}
            dynamicOptions={smartFilterDynamicOptions}
            initialItems={advancedFilterModel.items}
            onApply={(items) => handleAdvancedFilterChange(items)}
            context={{ businessUnitType: businessUnitType ?? null, businessUnitId: businessUnitId ?? null }}
          />
          {toggleBar}
        </Box>
      )}

      {/* Fallback toggle when no filter bar is shown (no businessUnitId in kanban) */}
      {viewMode === 'kanban' && !businessUnitId && toggleBar}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          px: viewMode === 'table' ? 2 : 1,
          py: 1,
        }}
      >
        {renderContent()}
      </Box>
    </div>
  );
};

export default Leads;
